import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, CreditCard, PenLine, Trash2, ChevronDown, ChevronUp, Shield, ShieldOff, MessageCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#FEFCFA] border border-[#E8E0DB] rounded-xl p-4 sm:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-full bg-[#A0655A]/10 text-[#A0655A] flex items-center justify-center">
          <Icon size={16} />
        </div>
        <span className="text-xs text-[#999] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-medium text-[#0A0A0A]">{value}</p>
      {sub && <p className="text-xs text-[#999] mt-0.5">{sub}</p>}
    </div>
  );
}

function UserRow({ user, onTogglePaid, onDelete, onExpand, isExpanded }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    if (isExpanded) { onExpand(null); return; }
    onExpand(user.id);
    if (!detail) {
      setLoadingDetail(true);
      try {
        const { data } = await API.get(`/admin/users/${user.id}`);
        setDetail(data);
      } catch { toast.error("Kunde inte hämta detaljer"); }
      setLoadingDetail(false);
    }
  };

  const date = user.created_at ? new Date(user.created_at).toLocaleDateString('sv-SE') : '–';
  const paidDate = user.paid_at ? new Date(user.paid_at).toLocaleDateString('sv-SE') : null;

  return (
    <div className="border border-[#E8E0DB]/60 rounded-xl overflow-hidden" data-testid={`user-row-${user.id}`}>
      <button onClick={handleExpand} className="w-full flex items-center gap-3 p-3.5 sm:p-4 text-left hover:bg-[#FEFCFA]/50 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-[#0A0A0A] truncate">{user.name || '(inget namn)'}</span>
            {user.role === 'admin' && (
              <span className="text-[9px] bg-[#A0655A]/10 text-[#A0655A] px-1.5 py-0.5 rounded font-medium uppercase">Admin</span>
            )}
          </div>
          <p className="text-xs text-[#777] truncate">{user.email}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${user.has_paid ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            {user.has_paid ? 'Betald' : 'Ej betald'}
          </span>
          <span className="text-xs text-[#999] hidden sm:inline">{date}</span>
          <span className="text-xs text-[#999]">{user.completed_steps}/9</span>
          {isExpanded ? <ChevronUp size={14} className="text-[#999]" /> : <ChevronDown size={14} className="text-[#999]" />}
        </div>
      </button>
      {isExpanded && (
        <div className="border-t border-[#E8E0DB]/40 p-4 bg-[#FEFCFA]/30">
          {loadingDetail ? (
            <p className="text-sm text-[#999]">Laddar...</p>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Registrerad</p><p className="text-sm text-[#0A0A0A]">{date}</p></div>
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Betalning</p><p className="text-sm text-[#0A0A0A]">{paidDate || '–'}</p></div>
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Reflektioner</p><p className="text-sm text-[#0A0A0A]">{detail.reflections_count}</p></div>
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Dagboksinlägg</p><p className="text-sm text-[#0A0A0A]">{detail.diary_entries_count}</p></div>
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Tester gjorda</p><p className="text-sm text-[#0A0A0A]">{detail.test_results_count}</p></div>
                <div><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Incheckning</p><p className="text-sm text-[#0A0A0A]">{detail.has_checkin ? 'Ja' : 'Nej'}</p></div>
                <div className="col-span-2"><p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">Slutförda steg</p><p className="text-sm text-[#0A0A0A]">{detail.completed_layers?.join(', ') || 'Inga'}</p></div>
              </div>
              {user.role !== 'admin' && (
                <div className="flex gap-2 pt-2 border-t border-[#E8E0DB]/30">
                  <Button onClick={() => onTogglePaid(user.id, !user.has_paid)} variant="outline" size="sm" className="text-xs rounded-lg" data-testid={`toggle-paid-${user.id}`}>
                    {user.has_paid ? <ShieldOff size={13} className="mr-1.5" /> : <Shield size={13} className="mr-1.5" />}
                    {user.has_paid ? 'Ta bort tillgång' : 'Ge tillgång'}
                  </Button>
                  <Button onClick={() => onDelete(user.id, user.name || user.email)} variant="outline" size="sm" className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-user-${user.id}`}>
                    <Trash2 size={13} className="mr-1.5" /> Ta bort
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ review, onApprove, onDelete }) {
  const date = review.created_at ? new Date(review.created_at).toLocaleDateString('sv-SE') : '–';
  return (
    <div className={`border rounded-xl p-4 ${review.approved ? 'border-[#E8E0DB]/60 bg-white' : 'border-amber-200 bg-amber-50/30'}`} data-testid={`review-admin-${review.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-[#0A0A0A]">{review.display_name}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase ${review.approved ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-100 text-amber-700'}`}>
              {review.approved ? 'Publicerad' : 'Väntar'}
            </span>
            {review.is_seed && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F0E8E3] text-[#999] uppercase">Seed</span>}
          </div>
          <p className="text-xs text-[#999]">{review.user_id} · {date}</p>
        </div>
      </div>
      <p className="text-sm text-[#333] leading-relaxed mb-3 italic">"{review.text}"</p>
      <div className="flex gap-2">
        {!review.approved && (
          <Button onClick={() => onApprove(review.id, true)} variant="outline" size="sm" className="text-xs rounded-lg text-emerald-600 hover:bg-emerald-50" data-testid={`approve-review-${review.id}`}>
            <Check size={13} className="mr-1" /> Godkänn
          </Button>
        )}
        {review.approved && !review.is_seed && (
          <Button onClick={() => onApprove(review.id, false)} variant="outline" size="sm" className="text-xs rounded-lg text-amber-600 hover:bg-amber-50" data-testid={`unpublish-review-${review.id}`}>
            Avpublicera
          </Button>
        )}
        <Button onClick={() => onDelete(review.id)} variant="outline" size="sm" className="text-xs rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-review-${review.id}`}>
          <Trash2 size={13} className="mr-1" /> Ta bort
        </Button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('users');
  const [reviewFilter, setReviewFilter] = useState('all');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/dashboard'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, reviewsRes] = await Promise.all([
        API.get('/admin/stats'),
        API.get('/admin/users'),
        API.get('/admin/reviews'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setReviews(reviewsRes.data.reviews);
    } catch {
      toast.error("Kunde inte ladda admin-data");
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const togglePaid = async (userId, newStatus) => {
    try {
      await API.patch(`/admin/users/${userId}`, { has_paid: newStatus });
      toast.success(newStatus ? 'Tillgång given' : 'Tillgång borttagen');
      fetchData();
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Vill du verkligen ta bort ${name}? All data raderas permanent.`)) return;
    try {
      await API.delete(`/admin/users/${userId}`);
      toast.success("Användare borttagen");
      setExpandedUser(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte ta bort"); }
  };

  const approveReview = async (reviewId, approved) => {
    try {
      await API.patch(`/admin/reviews/${reviewId}`, { approved });
      toast.success(approved ? 'Recension godkänd' : 'Recension avpublicerad');
      fetchData();
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Vill du ta bort denna recension permanent?')) return;
    try {
      await API.delete(`/admin/reviews/${reviewId}`);
      toast.success("Recension borttagen");
      fetchData();
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const filteredUsers = filter === 'all' ? users : filter === 'paid' ? users.filter(u => u.has_paid) : users.filter(u => !u.has_paid);
  const pendingReviews = reviews.filter(r => !r.approved);
  const filteredReviews = reviewFilter === 'all' ? reviews : reviewFilter === 'pending' ? reviews.filter(r => !r.approved) : reviews.filter(r => r.approved);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      <Navigation />
      <div className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium text-[#A0655A] mb-2">Administration</p>
            <h1 className="heading-font text-2xl sm:text-3xl font-medium text-[#0A0A0A]" data-testid="admin-title">Hantera</h1>
          </motion.div>

          {/* Stats */}
          {stats && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <StatCard icon={Users} label="Användare" value={stats.total_users} sub={`${stats.paid_users} betalda`} />
              <StatCard icon={CreditCard} label="Betalda" value={stats.paid_users} />
              <StatCard icon={PenLine} label="Reflektioner" value={stats.total_reflections} />
              <StatCard icon={MessageCircle} label="Recensioner" value={stats.total_reviews} sub={`${pendingReviews.length} väntar`} />
            </motion.div>
          )}

          {/* Tabs */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex gap-1 mb-6 border-b border-[#E8E0DB]/50 pb-px">
            {[
              { key: 'users', label: 'Användare', count: users.length },
              { key: 'reviews', label: 'Recensioner', count: reviews.length, badge: pendingReviews.length },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                  tab === t.key ? 'border-[#A0655A] text-[#A0655A]' : 'border-transparent text-[#999] hover:text-[#555]'
                }`}
                data-testid={`tab-${t.key}`}>
                {t.label}
                {t.badge > 0 && (
                  <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{t.badge}</span>
                )}
              </button>
            ))}
          </motion.div>

          {/* Users tab */}
          {tab === 'users' && (
            <>
              <div className="flex gap-1.5 mb-5">
                {[
                  { key: 'all', label: 'Alla', count: users.length },
                  { key: 'paid', label: 'Betalda', count: users.filter(u => u.has_paid).length },
                  { key: 'unpaid', label: 'Ej betalda', count: users.filter(u => !u.has_paid).length },
                ].map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filter === f.key ? 'bg-[#A0655A] text-white' : 'bg-[#F5F0EB] text-[#777] hover:text-[#0A0A0A]'
                    }`}
                    data-testid={`filter-${f.key}`}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {filteredUsers.length === 0 ? (
                  <p className="text-sm text-[#999] text-center py-8">Inga användare att visa.</p>
                ) : filteredUsers.map(u => (
                  <UserRow key={u.id} user={u} onTogglePaid={togglePaid} onDelete={deleteUser} onExpand={setExpandedUser} isExpanded={expandedUser === u.id} />
                ))}
              </div>
            </>
          )}

          {/* Reviews tab */}
          {tab === 'reviews' && (
            <>
              <div className="flex gap-1.5 mb-5">
                {[
                  { key: 'all', label: 'Alla', count: reviews.length },
                  { key: 'pending', label: 'Väntar', count: pendingReviews.length },
                  { key: 'approved', label: 'Publicerade', count: reviews.filter(r => r.approved).length },
                ].map(f => (
                  <button key={f.key} onClick={() => setReviewFilter(f.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      reviewFilter === f.key ? 'bg-[#A0655A] text-white' : 'bg-[#F5F0EB] text-[#777] hover:text-[#0A0A0A]'
                    }`}
                    data-testid={`review-filter-${f.key}`}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
              <div className="space-y-2.5">
                {filteredReviews.length === 0 ? (
                  <p className="text-sm text-[#999] text-center py-8">Inga recensioner att visa.</p>
                ) : filteredReviews.map(r => (
                  <ReviewCard key={r.id} review={r} onApprove={approveReview} onDelete={deleteReview} />
                ))}
              </div>
            </>
          )}

        </div>
      </div>
      <Footer />
    </div>
  );
}
